import { useEffect, useMemo, useState } from 'react';
import { getCardAttributes, getPublicCardAttributes } from '../services/cardAttributesService';
import type { CardAttributesResponse, CardAttributeValue } from '../services/cardAttributesService';
import { dedupeOptionsByValue } from '../utils/selectOptions';

/**
 * Mantine Select-compatible option format.
 */
interface SelectOption {
  value: string;
  label: string;
}

/**
 * Template-only audience meaning "works for any relationship". It is not a contact relationship and is
 * never returned by the card-attributes persona group.
 */
export const UNIVERSAL_TEMPLATE_AUDIENCE = 'Universal';

/**
 * Scoring weights keyed by category, used by the recommendation algorithm.
 * Emotional-angle and secondary-tag pillars were retired alongside their
 * underlying Contact / CardTemplate columns (#955 simplify-card-preferences).
 */
export interface MatchWeights {
  tone: number;
  mediaType: number;
  visualStyle: number;
}

/**
 * Return type of the useCardAttributes hook.
 */
export interface CardAttributesData {
  /** Active contact relationships (admin-managed catalog), A-Z. Never includes Universal. */
  personas: SelectOption[];
  tones: SelectOption[];
  mediaTypes: SelectOption[];
  visualStyles: SelectOption[];
  weights: MatchWeights;
  isLoading: boolean;
}

// Module-level cache prevents redundant API calls across components.
// Separate caches for authenticated and public endpoints.
let cachedData: CardAttributesResponse | null = null;
let cachePromise: Promise<CardAttributesResponse> | null = null;
let cachedPublicData: CardAttributesResponse | null = null;
let publicCachePromise: Promise<CardAttributesResponse> | null = null;

/**
 * Drops the cached card-attribute responses so the next consumer re-fetches. Called after an admin changes
 * the relationship catalog so open dropdowns in this tab are not stale. Other tabs and other users pick up
 * changes on their next load because the API does not cache the response.
 */
export function clearCardAttributesCache(): void {
  cachedData = null;
  cachePromise = null;
  cachedPublicData = null;
  publicCachePromise = null;
}

/**
 * Maps a category's values to Mantine Select options.
 *
 * Attribute names are admin-entered and not unique, and Mantine throws on
 * duplicate option values — a throw that unmounts the page. Dedupe here.
 */
function toSelectOptions(values: CardAttributeValue[]): SelectOption[] {
  return dedupeOptionsByValue(values.map((v) => ({ value: v.name, label: v.name })));
}

/**
 * Contact relationship options. The API never sends the template-only Universal audience in this group;
 * it is filtered defensively so a stray value can never be offered (or duplicated) on a contact.
 */
function toRelationshipOptions(values: CardAttributeValue[]): SelectOption[] {
  return toSelectOptions(
    values.filter((v) => v.name.toLowerCase() !== UNIVERSAL_TEMPLATE_AUDIENCE.toLowerCase()),
  );
}

const defaultWeights: MatchWeights = { tone: 10, mediaType: 6, visualStyle: 3 };

/**
 * Shared React hook for fetching card attribute values from the API.
 *
 * Returns Mantine Select-compatible option arrays for each category,
 * scoring weights for the recommendation algorithm, and a loading state.
 *
 * Module-level cache ensures only one API call is made regardless of how
 * many components mount this hook simultaneously.
 *
 * @param isPublic When true, uses the public (unauthenticated) endpoint.
 *   Used by guest scheduling flows where the user hasn't signed in yet.
 *
 * Consumers: ContentGenerationWorkspace, ContactForm, CreateSystemTemplateModal,
 * EditSystemTemplateModal, ScheduleCardModal.
 */
export function useCardAttributes(isPublic?: boolean): CardAttributesData {
  const cache = isPublic ? cachedPublicData : cachedData;
  const [data, setData] = useState<CardAttributesResponse | null>(cache);
  const [isLoading, setIsLoading] = useState(!cache);

  useEffect(() => {
    const currentCache = isPublic ? cachedPublicData : cachedData;
    if (currentCache) {
      setData(currentCache);
      setIsLoading(false);
      return;
    }

    // Select the correct fetcher and promise cache based on auth mode
    const fetcher = isPublic ? getPublicCardAttributes : getCardAttributes;
    let promise = isPublic ? publicCachePromise : cachePromise;

    if (!promise) {
      promise = fetcher();
      if (isPublic) {
        publicCachePromise = promise;
      } else {
        cachePromise = promise;
      }
    }

    promise
      .then((result) => {
        if (isPublic) {
          cachedPublicData = result;
        } else {
          cachedData = result;
        }
        setData(result);
      })
      .catch((err) => {
        console.error('Failed to load card attributes:', err);
        if (isPublic) {
          publicCachePromise = null;
        } else {
          cachePromise = null;
        }
      })
      .finally(() => setIsLoading(false));
  }, [isPublic]);

  if (!data) {
    return {
      personas: [],
      tones: [],
      mediaTypes: [],
      visualStyles: [],
      weights: defaultWeights,
      isLoading,
    };
  }

  return {
    personas: data.persona ? toRelationshipOptions(data.persona.values) : [],
    tones: data.tone ? toSelectOptions(data.tone.values) : [],
    mediaTypes: data.mediaType ? toSelectOptions(data.mediaType.values) : [],
    visualStyles: data.visualStyle ? toSelectOptions(data.visualStyle.values) : [],
    weights: {
      tone: data.tone?.matchWeight ?? defaultWeights.tone,
      mediaType: data.mediaType?.matchWeight ?? defaultWeights.mediaType,
      visualStyle: data.visualStyle?.matchWeight ?? defaultWeights.visualStyle,
    },
    isLoading,
  };
}

/**
 * Options for a template's Primary Persona / content-generation audience: every active relationship plus the
 * template-only Universal audience (last). Shared by the create/edit template modals and the content workspace
 * so the catalog and the Universal rule live in one place.
 */
export function useTemplateAudienceOptions(): { options: SelectOption[]; isLoading: boolean } {
  const { personas, isLoading } = useCardAttributes();
  const options = useMemo(
    () => [...personas, { value: UNIVERSAL_TEMPLATE_AUDIENCE, label: UNIVERSAL_TEMPLATE_AUDIENCE }],
    [personas],
  );
  return { options, isLoading };
}
